import {
    Component,
    Injectable,
    inject,
    signal,
    HostListener
} from "@angular/core";

import {
    MAT_DIALOG_DATA,
    MatDialog,
    MatDialogModule,
    MatDialogRef,
} from "@angular/material/dialog";

import {
    CdkFixedSizeVirtualScroll,
    CdkVirtualForOf,
    CdkVirtualScrollViewport
} from "@angular/cdk/scrolling";

import {
    CommonModule
} from "@angular/common";

import {
    MatInputModule
} from "@angular/material/input";

import {
    MatButtonModule
} from "@angular/material/button";

import {
    lastValueFrom
} from "rxjs";

import {
    BaseDialogService,
    NonNullableConfigData,
    TilbyDialogContentComponent,
    TilbyDialogToolbarComponent,
} from "@tilby/tilby-ui-lib/components/tilby-dialog";

import {
    MatFormFieldModule
} from "@angular/material/form-field";

import {
    MatSelectModule
} from "@angular/material/select";

import {
    MatIconModule
} from "@angular/material/icon";

import {
    FormsModule,
    ReactiveFormsModule
} from "@angular/forms";

import {
    ConfigurationManagerService,
    ConnectionService,
    EntityManagerService,
    ThemeModeService
} from "src/app/core";


import {
    KeypadPinComponent
} from "src/app/shared/components/keypad-pin/keypad-pin.component";

import {
    UppercaseFirstLetterPipe
} from "src/app/pipes/uppercase-firstletter.pipe";

import {
    mobileCheck
} from "@tilby/tilby-ui-lib/utilities";

import {
    User
} from "src/app/models";
import { TranslateModule } from "@ngx-translate/core";
import { AlertDialogService } from "../alert-dialog";

type UserSessionsManagerDialogData = {
    options?: Omit<UserSessionsManagerDialogOptions, 'selectActiveUser'>,
    users: User[],
    activeUser?: User,
    enableBadgeLogin: boolean,
};

type UserSessionsManagerDialogOptions = {
    disableNewUser?: boolean,
    canDismiss?: boolean,
    selectActiveUser?: boolean
};

@Component({
    selector: 'user-sessions-manager',
    templateUrl: './user-sessions-manager-dialog.component.html',
    styleUrls: ['./user-sessions-manager-dialog.component.scss'],
    standalone: true,
    imports: [
        CommonModule,
        TilbyDialogContentComponent,
        TilbyDialogToolbarComponent,
        MatInputModule,
        MatDialogModule,
        MatFormFieldModule,
        MatSelectModule,
        MatIconModule,
        MatButtonModule,
        ReactiveFormsModule,
        FormsModule,
        CdkFixedSizeVirtualScroll,
        CdkVirtualForOf,
        CdkVirtualScrollViewport,
        KeypadPinComponent,
        UppercaseFirstLetterPipe,
        TranslateModule
    ],
})
export class UserSessionsManagerComponent {
    private readonly alertDialogService = inject(AlertDialogService);
    private readonly connection = inject(ConnectionService);
    private readonly matDialogRef = inject(MatDialogRef);
    protected readonly configurationManagerService = inject(ConfigurationManagerService);
    protected readonly data: UserSessionsManagerDialogData = inject(MAT_DIALOG_DATA);
    protected readonly themeMode = inject(ThemeModeService);

    isMobile = mobileCheck();
    private users = this.data.users;

    protected pincode: string = '';
    protected userFilter: string = '';
    protected pinDigitClass: string[] = new Array(4).fill('');
    protected usersFiltered = this.users;
    protected userSelected?: User;
    protected hideUsers = false;
    protected openFilter = false;
    protected openPinKeypad = false;
    protected blockPinKeypad = false;
    protected canDismiss = !!this.data.options?.canDismiss;
    protected showNewUser = this.data.options?.disableNewUser ? signal(false) : this.connection.isOnline;
    protected enableBadgeLogin = this.data.enableBadgeLogin;
    protected showLoginFromBadge = false;
    protected showBackButton: boolean = false;
    protected inputBuffer: string = '';
    protected isBadgeIdError: boolean = false;
    protected lastTriggerTime: number = 0;

    constructor () {
        const activeUser = this.data.activeUser;
    
        if (activeUser) {
            this.userSelected = activeUser;
            this.hideUsers = true;
        }

        if(this.enableBadgeLogin) {
            this.changeView('badge');
        }
    }

    changeView(view: 'badge' | 'pin') {
        if(view === 'badge') {
            this.showLoginFromBadge = true;
            this.showBackButton = false;
            this.deselectUserSession();
        } else if(view === 'pin') {
            this.showLoginFromBadge = false;
            this.showBackButton = true;
        }
    }

    userFilterOpen() {
        this.openFilter = !this.openFilter;

        if (!this.openFilter) {
            this.userFilter = '';
        } else {
            setTimeout(() => {
                const userFilterInput = document.getElementById('userFilterInput') as HTMLElement;

                if (userFilterInput) {
                    userFilterInput.focus();
                }
            }, 100)
        }
    }

    clearUserFilter() {
        this.openFilter = false;
        this.userFilter = '';
        this.usersFiltered = this.users;
    }

    selectUserSession(user: User) {
        this.clearPinField();
        this.userSelected = user;

        if (!user.pin) {
            this.returnUser(user);
        } else {
            this.openPinKeypad = true;
            this.matDialogRef.updateSize('505px', '465px');
        }

        this.openFilter = false;
        this.userFilter = '';
        this.usersFiltered = this.users;
    };

    deselectUserSession() {
        // logout: no show users'list
        if(this.hideUsers) {
            this.clearPinField();
            return;
        }

        // login
        if(this.userSelected && this.userSelected.pin) {
            this.userSelected = undefined;
            this.openPinKeypad = false;
        }

        this.clearPinField();
        this.matDialogRef.updateSize('300px', '465px');
    }

    clearPinField() {
        this.pincode = '';
        this.pinDigitClass.fill('');
    };

    /**
     * Handle the change of the pincode input.
     * @param pin the current pincode
     */
    onDigitChange(pin: string) {
        if (!pin) {
            // If the pincode is empty, clear the pin digit
            return this.clearPinField();
        }

        // If the user is not selected or the pin keypad is blocked, clear the pincode
        if (!this.userSelected || this.blockPinKeypad) {
            this.pincode = '';
            return;
        }

        // Notify the user that the digits are being entered
        for (let i = 0; i < pin.length; i++) {
            this.pinDigitClass[i] = 'circle-pin-correct';
        }

        // Don't proceed if the pincode is not 4 digits
        if (pin.length !== 4) {
            return;
        }

        // If the pincode is incorrect, set the pin digit class to incorrect
        if (pin !== this.userSelected.pin) {
            this.pinDigitClass.fill('circle-pin-incorrect');

            // Block the pin keypad for 2 seconds
            this.blockPinKeypad = true;

            setTimeout(() => {
                this.clearPinField();
                this.blockPinKeypad = false;
            }, 2000);

            return;
        }

        // If the pincode is correct, return the user to the caller
        this.returnUser(this.userSelected);
    }

    onFilterChange(event: any) {
        this.userFilter = event.target.value;

        // Reset the filtered users
        this.usersFiltered = this.users;
        
        if(!this.userFilter) {
            return;
        }

        const lowercaseFilter = this.userFilter.toLowerCase().split(' ');

        for(const word of lowercaseFilter) {
            this.usersFiltered = this.usersFiltered.filter(user => user.first_name?.toLowerCase().includes(word) || user.last_name?.toLowerCase().includes(word));
        }
    };

    returnUser(user: User) {
        this.matDialogRef.close(user);
    };

    newLogin() {
        this.matDialogRef.close();
    };

    @HostListener('window:keydown', ['$event'])
    checkBadgeId(event: KeyboardEvent) {
        if (!this.showLoginFromBadge) {
            return;
        }

        if (!this.isBadgeIdError || event.key !== '') {
            event.preventDefault();
        }

        const now = Date.now();

        if (this.isBadgeIdError || (now - this.lastTriggerTime < 1000)) {
            return;
        }

        if (event.key === 'Enter') {
            this.lastTriggerTime = now;

            const code = this.inputBuffer.trim();
            this.inputBuffer = '';

            if (code) {
                this.verifyBadge(code);
            }

            return;
        }

        if (event.key.length === 1) {
            this.inputBuffer += event.key;
        }
    }
    
    private verifyBadge(code: string) {
        if (!code) {
            return;
        }

        const users = this.data.activeUser ? [this.data.activeUser] : this.users;
        const userFound = users.find(user => user.badge_id === code);
        
        if (userFound) {
            this.returnUser(userFound);
        } else {
            this.isBadgeIdError = true;

            this.alertDialogService.openDialog({ data: { messageLabel: 'DIALOG.USER_SESSIONS_MANAGER.LOGIN_WITH_CARD_ERROR' } }).finally(() => {
                this.isBadgeIdError = false;
            });
        }

        this.inputBuffer = '';
    }
}

@Injectable({
    providedIn: "root",
})
export class UserSessionsManagerDialogService extends BaseDialogService {
    private readonly dialogRef = inject(MatDialog);
    private readonly entityManager = inject(EntityManagerService);
    private readonly configurationManagerService = inject(ConfigurationManagerService);

    public async openDialog(config?: NonNullableConfigData<{ options?: UserSessionsManagerDialogOptions }>): Promise<User | undefined | ''> {
        const enableBadgeLogin = !!(await this.configurationManagerService.getEarlyShopPreference('users.enable_user_login_from_badge'));
        const users =  await this.entityManager.userSessions.getCollection();
        let activeUser: User | undefined;

        if(config?.data.options?.selectActiveUser) {
            // If user doesn't have the pin field, skip check
            activeUser = users.find(user => user.active);
    
            if(activeUser) {

                if(!activeUser.pin && (!enableBadgeLogin || !activeUser.badge_id)) {
                    return activeUser;
                }
            }
        }

        const configEdited: NonNullableConfigData<UserSessionsManagerDialogData> = {
            ...config,
            ...this.switchMobileDesktopDimensions({ minWidth: '300px', height: '465px' }),
            disableClose: true,
            data: {
                options: {
                    disableNewUser: !!(config?.data.options?.disableNewUser),
                    canDismiss: !!(config?.data.options?.canDismiss)
                },
                enableBadgeLogin: enableBadgeLogin,
                activeUser: activeUser,
                users: users
            },
        };

        return lastValueFrom(
            this.dialogRef.open(UserSessionsManagerComponent, configEdited).afterClosed()
        );
    }

    /**
     * @deprecated needed for backwards compatibility, use `openDialog` instead
     */
    public show(options?: UserSessionsManagerDialogOptions): Promise<User | undefined> {
        return this.openDialog({
            data: {
                options
            }
        }).then((result) => result === '' ? Promise.reject() : result);
    }
}
